import { lazy, Suspense, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { useAuthStore } from "@/store/auth";

import Layout from "@/components/layout/Layout";
import AdminLayout from "@/components/layout/AdminLayout";
import SellerLayout from "@/components/layout/SellerLayout";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import PageSpinner from "@/components/ui/page-spinner";
import ErrorBoundary from "@/components/ErrorBoundary";

// ── Lazy-loaded pages (code splitting) ───────────────
const HomePage = lazy(() => import("@/pages/HomePage"));
const CatalogPage = lazy(() => import("@/pages/CatalogPage"));
const ProductPage = lazy(() => import("@/pages/ProductPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const MyOrdersPage = lazy(() => import("@/pages/MyOrdersPage"));
const CartPage = lazy(() => import("@/pages/CartPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

// Seller pages
const SellerOrdersPage = lazy(() => import("@/pages/seller/SellerOrdersPage"));
const SellerDeliveryPage = lazy(() => import("@/pages/seller/SellerDeliveryPage"));
const SellerReturnsPage = lazy(() => import("@/pages/seller/SellerReturnsPage"));
const SellerStockPage = lazy(() => import("@/pages/seller/SellerStockPage"));
const SellerReportPage = lazy(() => import("@/pages/seller/SellerReportPage"));

// Shared pages
const UserDeliveryPage = lazy(() => import("@/pages/shared/UserDeliveryPage"));

// Admin pages
const DashboardPage = lazy(() => import("@/pages/admin/DashboardPage"));
const GoodsPage = lazy(() => import("@/pages/admin/GoodsPage"));
const OrdersPage = lazy(() => import("@/pages/admin/OrdersPage"));
const ReturnsPage = lazy(() => import("@/pages/admin/ReturnsPage"));
const DeliveryPage = lazy(() => import("@/pages/admin/DeliveryPage"));
const ReportsPage = lazy(() => import("@/pages/admin/ReportsPage"));
const UsersPage = lazy(() => import("@/pages/admin/UsersPage"));
const PublishPage = lazy(() => import("@/pages/admin/PublishPage"));
const SettingsPage = lazy(() => import("@/pages/admin/SettingsPage"));

export default function App() {
  const fetchUser = useAuthStore((s) => s.fetchUser);

  useEffect(() => {
    void fetchUser();
  }, [fetchUser]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          {/* Public routes */}
          <Route element={<Layout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* Auth-required */}
            <Route element={<ProtectedRoute />}>
              <Route path="/catalog" element={<CatalogPage />} />
              <Route path="/catalog/:number" element={<ProductPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/orders" element={<MyOrdersPage />} />
            </Route>

            {/* 404 catch-all inside layout */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Seller routes (seller + admin) */}
          <Route element={<ProtectedRoute requiredRole="seller" />}>
            <Route element={<SellerLayout />}>
              <Route path="/seller" element={<SellerOrdersPage />} />
              <Route path="/seller/orders" element={<SellerOrdersPage />} />
              <Route path="/seller/delivery" element={<SellerDeliveryPage />} />
              <Route path="/seller/delivery/:userId" element={<UserDeliveryPage />} />
              <Route path="/seller/returns" element={<SellerReturnsPage />} />
              <Route path="/seller/stock" element={<SellerStockPage />} />
              <Route path="/seller/report" element={<SellerReportPage />} />
            </Route>
          </Route>

          {/* Admin routes (admin only) */}
          <Route element={<ProtectedRoute requiredRole="admin" />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<DashboardPage />} />
              <Route path="/admin/goods" element={<GoodsPage />} />
              <Route path="/admin/orders" element={<OrdersPage />} />
              <Route path="/admin/delivery" element={<DeliveryPage />} />
              <Route path="/admin/delivery/:userId" element={<UserDeliveryPage />} />
              <Route path="/admin/returns" element={<ReturnsPage />} />
              <Route path="/admin/reports" element={<ReportsPage />} />
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/publish" element={<PublishPage />} />
              <Route path="/admin/settings" element={<SettingsPage />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
